interface SparklinePoint {
  t: number;
  v: number;
}

interface SparklineProps {
  data: SparklinePoint[];
  height?: number;
  color?: string;
  fillFrom?: string;
  fillTo?: string;
  windowMs?: number;
  yMin?: number;
  yMax?: number;
  formatY?: (value: number) => string;
  ariaLabel?: string;
}

export function Sparkline({
  data,
  height = 80,
  color = "var(--color-accent)",
  fillFrom,
  fillTo = "transparent",
  windowMs,
  yMin,
  yMax,
  formatY,
  ariaLabel,
}: SparklineProps) {
  const width = 600;
  const padX = 4;
  const padTop = 6;
  const padBottom = 4;
  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;

  if (data.length === 0) {
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="sparkline sparkline--empty"
        role="img"
        aria-label={ariaLabel ?? "No data yet"}
      >
        <rect x={0} y={0} width={width} height={height} fill="transparent" />
      </svg>
    );
  }

  const lastPoint = data[data.length - 1]!;
  const firstPoint = data[0]!;
  const now = lastPoint.t;
  const earliest = windowMs ? now - windowMs : firstPoint.t;
  const span = Math.max(1, now - earliest);

  const visibleData = data.filter((p) => p.t >= earliest);
  const series = visibleData.length > 0 ? visibleData : data.slice(-1);

  const computedMin = yMin ?? Math.min(...series.map((p) => p.v), 0);
  const computedMax = yMax ?? Math.max(...series.map((p) => p.v), computedMin + 1);
  const range = Math.max(1, computedMax - computedMin);

  function xFor(t: number): number {
    return padX + ((t - earliest) / span) * innerW;
  }

  function yFor(v: number): number {
    return padTop + (1 - (v - computedMin) / range) * innerH;
  }

  const projected = series.map((p) => ({ x: xFor(p.t), y: yFor(p.v) }));
  const yMaxClamp = padTop + innerH;
  const linePath = buildSmoothPath(projected, yMaxClamp);

  const seriesFirst = projected[0]!;
  const seriesLast = projected[projected.length - 1]!;
  const areaPath = projected.length > 1
    ? `${linePath} L${seriesLast.x.toFixed(2)} ${yMaxClamp.toFixed(2)} L${seriesFirst.x.toFixed(2)} ${yMaxClamp.toFixed(2)} Z`
    : null;

  const latest = seriesLast;
  const gradientId = `sparkline-fill-${Math.random().toString(36).slice(2, 9)}`;

  const yLabelTop = computedMax;
  const yLabelBottom = computedMin;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="sparkline"
      role="img"
      aria-label={ariaLabel ?? "time series chart"}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillFrom ?? color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={fillTo} stopOpacity={0} />
        </linearGradient>
      </defs>
      <line
        x1={padX}
        x2={width - padX}
        y1={padTop + innerH}
        y2={padTop + innerH}
        stroke="rgba(255,255,255,0.05)"
        strokeWidth={1}
      />
      {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
      {latest && (
        <circle cx={latest.x} cy={latest.y} r={2.4} fill={color} />
      )}
      {formatY && (
        <>
          <text x={width - padX} y={padTop + 4} textAnchor="end" fontSize={9} fill="rgba(220,224,230,0.4)">
            {formatY(yLabelTop)}
          </text>
          <text x={width - padX} y={padTop + innerH} textAnchor="end" fontSize={9} fill="rgba(220,224,230,0.4)">
            {formatY(yLabelBottom)}
          </text>
        </>
      )}
    </svg>
  );
}

interface ProjectedPoint { x: number; y: number; }

function buildSmoothPath(points: ProjectedPoint[], yMaxClamp: number): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    const p = points[0]!;
    return `M${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }
  const tension = 0.6;
  const segments: string[] = [];
  const start = points[0]!;
  segments.push(`M${start.x.toFixed(2)} ${start.y.toFixed(2)}`);
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[Math.max(0, i - 1)]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[Math.min(points.length - 1, i + 2)]!;
    const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6;
    let cp1y = p1.y + ((p2.y - p0.y) * tension) / 6;
    const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6;
    let cp2y = p2.y - ((p3.y - p1.y) * tension) / 6;
    cp1y = clampToBaseline(cp1y, yMaxClamp);
    cp2y = clampToBaseline(cp2y, yMaxClamp);
    segments.push(
      `C${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
    );
  }
  return segments.join(" ");
}

function clampToBaseline(y: number, yMaxClamp: number): number {
  if (y > yMaxClamp) return yMaxClamp;
  return y;
}
